import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_DIALOG_PAGE_SIZE, getDisplayText, matchesExactCode, parseSearchCodes, resolveRowId } from '../helpers';
import type { ResolveResult, SearchRequestFn } from '../types';

interface UseMultipleSearchValueParams<T> {
  columnDisplay?: keyof T;
  onRowSelection?: (rows: T[]) => void;
  onValueChange?: (value: any) => void;
  pageSize?: number;
  rows?: T[];
  searchRequest: SearchRequestFn<T> | null;
  separator?: string;
  value?: any;
}

export const useMultipleSearchValue = <T>({
  columnDisplay,
  onRowSelection,
  onValueChange,
  pageSize = DEFAULT_DIALOG_PAGE_SIZE,
  rows,
  searchRequest,
  separator = ',',
  value
}: UseMultipleSearchValueParams<T>) => {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInputDirty, setIsInputDirty] = useState(false);

  useEffect(() => {
    if (!Array.isArray(value)) {
      return;
    }

    setSelectedItems(value);

    if (!isInputDirty) {
      setInputValue(
        value
          .map(item => getDisplayText(item, columnDisplay as string))
          .filter(Boolean)
          .join(', ')
      );
    }

    if (value.length === 0) {
      setError(null);
    }
  }, [columnDisplay, isInputDirty, value]);

  const updateSelection = useCallback(
    (nextItems: T[]) => {
      setSelectedItems(nextItems);
      setInputValue(nextItems.map(item => getDisplayText(item, columnDisplay as string)).join(', '));
      setIsInputDirty(false);
      setError(null);
      onRowSelection?.(nextItems);
    },
    [columnDisplay, onRowSelection]
  );

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    setInputValue('');
    setError(null);
    setIsInputDirty(false);
    onRowSelection?.([]);
  }, [onRowSelection]);

  const fetchByCodes = useCallback(
    async (nextInputValue: string): Promise<ResolveResult<T>> => {
      const codes = parseSearchCodes(nextInputValue, separator);

      if (codes.length === 0) {
        return { items: [], notFoundCodes: [] };
      }

      setIsSearching(true);
      setError(null);

      try {
        const resolvedMap = new Map<string, T>();
        const notFoundCodes: string[] = [];

        if (rows?.length && !searchRequest) {
          codes.forEach(code => {
            const matchedRow = rows.find(row => matchesExactCode(row, columnDisplay, code));

            if (!matchedRow) {
              notFoundCodes.push(code);
              return;
            }

            resolvedMap.set(resolveRowId(matchedRow), matchedRow);
          });
        } else if (searchRequest) {
          const results = await Promise.all(
            codes.map(async code => {
              try {
                const response = await searchRequest({
                  page: 0,
                  pageSize,
                  query: code
                });

                const exactMatch = response.items.find(item => matchesExactCode(item, columnDisplay, code));

                return { code, item: exactMatch ?? null };
              } catch {
                return { code, item: null };
              }
            })
          );

          results.forEach(result => {
            if (!result.item) {
              notFoundCodes.push(result.code);
              return;
            }

            resolvedMap.set(resolveRowId(result.item), result.item);
          });
        } else {
          return { items: [], notFoundCodes: codes };
        }

        if (notFoundCodes.length > 0) {
          setError(`Không tìm thấy: ${notFoundCodes.join(', ')}`);
        }

        return {
          items: Array.from(resolvedMap.values()),
          notFoundCodes
        };
      } catch {
        setError('Có lỗi xảy ra khi tìm kiếm');
        return { items: [], notFoundCodes: codes };
      } finally {
        setIsSearching(false);
      }
    },
    [columnDisplay, pageSize, rows, searchRequest, separator]
  );

  const handleInputChange = useCallback(
    (nextInputValue: string) => {
      setInputValue(nextInputValue);
      setIsInputDirty(true);
      onValueChange?.(nextInputValue);
    },
    [onValueChange]
  );

  const handleSearch = useCallback(async (): Promise<ResolveResult<T>> => {
    if (!inputValue.trim()) {
      clearSelection();
      return { items: [], notFoundCodes: [] };
    }

    const result = await fetchByCodes(inputValue);

    if (result.notFoundCodes.length === 0) {
      updateSelection(result.items);
    }

    return result;
  }, [clearSelection, fetchByCodes, inputValue, updateSelection]);

  const resolveCurrentInput = useCallback(async (): Promise<ResolveResult<T>> => {
    if (!inputValue.trim()) {
      clearSelection();
      return { items: [], notFoundCodes: [] };
    }

    const currentCodes = selectedItems.map(item => getDisplayText(item, columnDisplay as string).toLowerCase());
    const inputCodes = parseSearchCodes(inputValue, separator).map(code => code.toLowerCase());
    const allCodesResolved = inputCodes.every(code => currentCodes.includes(code));

    if (allCodesResolved && inputCodes.length === currentCodes.length) {
      setError(null);
      return { items: selectedItems, notFoundCodes: [] };
    }

    const result = await fetchByCodes(inputValue);

    if (result.notFoundCodes.length === 0) {
      updateSelection(result.items);
    }

    return result;
  }, [clearSelection, columnDisplay, fetchByCodes, inputValue, selectedItems, separator, updateSelection]);

  const appendSelection = useCallback(
    (row: T | null) => {
      if (!row) {
        return selectedItems;
      }

      const nextItems = selectedItems.some(item => resolveRowId(item) === resolveRowId(row))
        ? selectedItems
        : [...selectedItems, row];

      updateSelection(nextItems);
      return nextItems;
    },
    [selectedItems, updateSelection]
  );

  const replaceSelection = useCallback(
    (nextItems: T[]) => {
      updateSelection(nextItems);
      return nextItems;
    },
    [updateSelection]
  );

  return {
    appendSelection,
    clearSelection,
    error,
    handleInputChange,
    handleSearch,
    inputValue,
    isSearching,
    replaceSelection,
    resolveCurrentInput,
    selectedItems,
    setError
  };
};
